import { useMemo } from "react";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { isGranted } from "@/lib/utils";
import { permManage, permView } from "@/lib/permissions";

// Reads the current user's effective permissions and exposes gating helpers.
//
// Full access in the UI is granted to managers (primary or secondary), owners,
// support and admin — mirroring the API's PermissionResolver. These users hold
// every permission on the restaurant, so we short-circuit to avoid regressions
// on a stale session that lacks `effectivePermissions`. What stays protected
// is mutating the PRIMARY manager (removal, PIN), which the manager endpoints
// enforce server-side.
export function usePermissions() {
  const currentUser = useAppSelector((state) => state.user.currentUser);

  const isFullAccess = isGranted(
    [Roles.manager, Roles.owner, Roles.support, Roles.admin],
    currentUser?.roles
  );

  const set = useMemo(
    () => new Set(currentUser?.effectivePermissions ?? []),
    [currentUser?.effectivePermissions]
  );

  return useMemo(() => {
    const can = (permission: string) => isFullAccess || set.has(permission);
    return {
      isFullAccess,
      /** True if the user has the exact permission value (e.g. "inventory.manage"). */
      can,
      /** True if the user has any of the given permission values. */
      canAny: (...permissions: string[]) => permissions.some(can),
      /** True if the user can view the domain (e.g. "inventory"). */
      canView: (domain: string) => can(permView(domain)),
      /** True if the user can manage (write) the domain. */
      canManage: (domain: string) => can(permManage(domain)),
    };
  }, [isFullAccess, set]);
}
